import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ProfileEditView from '../../components/ProfileEditView';
import AvatarUpload from '../../components/AvatarUpload';

const F = { fontFamily: 'Inter' } as const;

type View = 'profile' | 'edit-password';

const TeacherProfile: React.FC = () => {
  const { user } = useAuth();
  const [view, setView] = useState<View>('profile');

  // auth/me returns name=first_name, surname=last_name.
  // AuthContext composes user.name = "first last" (includes surname already).
  // Do NOT concatenate surname again — that causes "Ahmet Bingül Bingül".
  const fullName  = user?.name ?? '-'; // "Ahmet Bingül" — already correct
  const surname   = user?.surname ?? '';
  // Extract just the first part for the "Ad" field card
  const firstName = surname && fullName.endsWith(surname)
    ? fullName.slice(0, fullName.length - surname.length).trim() || fullName
    : fullName;
  const lastName  = surname || '-';
  const email     = user?.email ?? '-';
  const phone     = (user as any)?.phone ?? '-';

  // ── "Ders yetkisi", and it is NOT a branş ──────────────────────────────────
  //
  // `teacher_subjects` rows decide which GOAL DEFINITIONS this teacher can pick
  // (birey-backend teacher.routes.ts, /teacher/goal-definitions). It is an access
  // list, it is many-valued, and it is Digipo-local — a teacher whose branş is
  // Matematik may legitimately be given Biyoloji and Coğrafya goals too, which is
  // ordinary at a small kurum.
  //
  // ⚠️ IT WAS LABELLED "Branş" AND THAT WAS THE WHOLE CONFUSION. Once the real branş
  // arrived from Birey Kimlik the screen showed two fields called the same thing, and
  // they will routinely disagree because they answer different questions:
  //
  //   Branş         WHO the teacher is.  ONE value.  Birey Kimlik. Estate-wide.
  //   Ders yetkisi  WHAT they may pick.  MANY values. DigipoPanel. Local.
  //
  // Deriving one from the other is not possible in either direction: a single branş
  // cannot express a three-subject access list, and an access list cannot say which
  // of the three the person actually IS.
  //
  // ⛔ `teacher_profiles.specialization` IS NO LONGER SHOWN. It was the fallback here,
  // but it is free text with no vocabulary — a second, unvalidated answer to the branş
  // question that the green badge now answers authoritatively. Filled for 8 teachers
  // in production; the column is left alone, only the display is dropped. If those
  // eight need it back, it belongs beside the branş as a legacy value, not inside an
  // access list.
  const levelKey = (user as any)?.teacher_profiles?.school_level;
  const seviye   = levelKey === 'secondary' ? 'Ortaokul' : levelKey === 'highschool' ? 'Lise' : '-';

  // "Tüm Dersler" (rehberlik / sınıf öğretmeni) is not "every subject in the catalogue":
  // the library is still cut to the teacher's kademe (ortaokul 5-8, lise 9-12), so say
  // which one — "Tüm Dersler (Ortaokul)".
  const teacherSubjects: any[] = (user as any)?.teacher_subjects ?? [];
  const dersYetkisi = teacherSubjects.length > 0
    ? teacherSubjects
        .map((s: any) => (/^t[uü]m\s+dersler$/i.test(String(s.name).trim()) && seviye !== '-' ? `${s.name} (${seviye})` : s.name))
        .join(', ')
    : '-';

  // ── Öğretmen türü, mirrored from Birey Kimlik ──────────────────────────────
  //
  // The phrase is rendered SERVER-side (auth.service.ts#bransTitle) so it reads the
  // same everywhere and a sixteenth branş added at the IdP needs no deploy here. This
  // screen only decides whether to draw it.
  //
  // ⚠️ A SEPARATE BADGE FROM "Ders yetkisi", deliberately — see the note above for what
  // each one answers. This is the estate's single authoritative value, set once in
  // eis ekosistem and dispatched through the identity feed; that one is a local,
  // many-valued access list. They can disagree, and when they do both are correct.
  //
  // `bransLabel` is the bare branş ("Matematik") for the Bilgilerim row, where the
  // column heading already supplies the word; `title` is the full phrase
  // ("Matematik Öğretmeni") for the badge, which stands on its own.
  const ogretmenTuru: string | null = (user as any)?.birey_brans?.title ?? null;
  const bransLabel: string = (user as any)?.birey_brans?.label ?? '-';

  // Primary institution from teacher_profiles.institutions (via advisor_institution_id FK)
  const primaryInst: any = (user as any)?.primary_institution;
  const schoolName = primaryInst?.name ?? (user as any)?.institutions?.[0]?.name ?? '-';

  const profileFields = [
    { label: 'Ad',               value: firstName },
    { label: 'Soyad',            value: lastName || '-' },
    { label: 'E-posta',          value: email },
    { label: 'Telefon Numarası', value: phone },
    { label: 'Okul',             value: schoolName },
    { label: 'Branş',            value: bransLabel },
    { label: 'Ders yetkisi',     value: dersYetkisi },
    { label: 'Seviye',           value: seviye },
  ];

  // ── Edit view (standart düzen — ProfileEditView, öğrenciyle ortak) ─────────
  if (view === 'edit-password') {
    return (
      <ProfileEditView
        onBack={() => setView('profile')}
        readOnlyRows={[
          { label: 'Okul', value: schoolName },
          { label: 'Branş', value: bransLabel },
          { label: 'Ders yetkisi', value: dersYetkisi },
          { label: 'Seviye', value: seviye },
        ]}
      />
    );
  }

  // ── Main profile — standard layout: hero card on top, info section below ───
  return (
    <div className="max-w-[1100px]">
      <h1 className="mb-5" style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Profil</h1>

      {/* Hero card — full width: avatar + identity, action top-right */}
      <div className="bg-white rounded-[24px] p-6 mb-5 flex flex-col sm:flex-row sm:items-center gap-5"
        style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
        {/* AP35: avatar artık tıklanıp değiştirilebilir */}
        <AvatarUpload size={112} />
        <div className="flex-1 min-w-0">
          <p style={{ ...F, fontSize: 28, fontWeight: 600, color: '#2b2b2b', marginBottom: 2 }}>{fullName}</p>
          <p style={{ ...F, fontSize: 15, fontWeight: 500, color: '#6b6b6b', marginBottom: 10 }}>{schoolName}</p>
          <div className="flex flex-wrap gap-2">
            {/* Birey Kimlik's answer, first because it is the authoritative one. */}
            {ogretmenTuru && (
              <span className="rounded-full px-3 py-1" style={{ ...F, fontSize: 13, fontWeight: 600, color: '#1f7a5c', background: 'rgba(31,122,92,0.10)' }}>
                {ogretmenTuru}
              </span>
            )}
            {dersYetkisi !== '-' && (
              <span className="rounded-full px-3 py-1" style={{ ...F, fontSize: 13, fontWeight: 600, color: '#3973bf', background: 'rgba(57,115,191,0.10)' }}>
                {dersYetkisi}
              </span>
            )}
            {seviye !== '-' && (
              <span className="rounded-full px-3 py-1" style={{ ...F, fontSize: 13, fontWeight: 600, color: '#e5599b', background: 'rgba(229,89,155,0.10)' }}>
                {seviye}
              </span>
            )}
          </div>
        </div>
        <button onClick={() => setView('edit-password')}
          className="rounded-[16px] px-5 py-2.5 hover:opacity-90 self-start sm:self-center flex-shrink-0"
          style={{ background: '#e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Profili Düzenle</span>
        </button>
      </div>

      {/* Bilgilerim — full-width section below the hero */}
      <div className="bg-white rounded-[24px] p-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
        <h2 className="mb-5" style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Bilgilerim</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-5">
          {profileFields.map((f, i) => (
            <div key={i} className="min-w-0 border-b border-[#eeeeee] pb-3">
              <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#3973bf', marginBottom: 4 }}>{f.label}</p>
              <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b' }} className="break-words">{f.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeacherProfile;
